import React from 'react';
import { AlertCircle } from 'lucide-react';

interface RemoveExecutionConfirmModalProps {
  isOpen: boolean;
  nodeName: string;
  dataEdgeCount: number;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Modal to confirm removal of execution flow edge when it would orphan data connections
 */
export const RemoveExecutionConfirmModal: React.FC<RemoveExecutionConfirmModalProps> = ({
  isOpen,
  nodeName,
  dataEdgeCount,
  onConfirm,
  onCancel,
}) => {
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onCancel}
      data-testid="remove-execution-confirm-modal"
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 mb-4">
          <AlertCircle className="w-6 h-6 text-yellow-500" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Remove Execution Flow?
          </h3>
        </div>
        
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          Removing the execution flow to <strong>{nodeName}</strong> will also remove{' '}
          <strong>{dataEdgeCount}</strong> data connection{dataEdgeCount !== 1 ? 's' : ''} from this node.
        </p>
        
        <p className="text-sm text-gray-500 dark:text-gray-500 mb-6">
          Per the execution flow rules, nodes must be connected to the execution chain before they can have data connections.
        </p>
        
        <div className="flex gap-3 justify-end">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md transition-colors"
            data-testid="cancel-remove-button"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors"
            data-testid="confirm-remove-button"
          >
            Remove All
          </button>
        </div>
      </div>
    </div>
  );
};

